/**
 * Core type definitions for the engine
 */

import { DataType } from 'apache-arrow';
import { MaterializedTableData } from './table-data';

/**
 * SQL dialect supported by the engine
 */
export enum SQLDialect {
  DUCKDB = 'DUCKDB',
  SQLITE = 'SQLITE',
  POSTGRESQL = 'POSTGRESQL'
}

/**
 * Logical type for a column
 */
export enum LogicalType {
  TIMESTAMP = 'timestamp',
  DATE = 'date',
  CURRENCY = 'currency',
  PERCENTAGE = 'percentage'
}

/**
 * Physical column types based on Apache Arrow data types
 */
export enum PhysicalColumnType {
  // String types
  UTF8 = 'Utf8',
  STRING = 'String',
  
  // Numeric types
  INT32 = 'Int32',
  INT64 = 'Int64',
  FLOAT32 = 'Float32',
  FLOAT64 = 'Float64',
  
  // Boolean type
  BOOL = 'Bool',
  
  // Date and time types
  DATE = 'date',
  TIMESTAMP = 'timestamp',
  
  // Other types
  OBJECT = 'object',
  UNKNOWN = 'unknown'
}

/**
 * Helper function to check if a column type is a date type
 */
export function isDateType(type: string): boolean {
  return type === PhysicalColumnType.DATE || 
         type === PhysicalColumnType.TIMESTAMP || 
         type === 'Date' || 
         type === 'Date32' || 
         type === 'Date64';
}

/**
 * Helper function to check if a column type is a numeric type
 */
export function isNumericType(type: string): boolean {
  return type === PhysicalColumnType.INT32 || 
         type === PhysicalColumnType.INT64 || 
         type === PhysicalColumnType.FLOAT32 || 
         type === PhysicalColumnType.FLOAT64 ||
         type === 'Int' ||
         type === 'Float';
}

/**
 * Helper function to check if a column type is a string type
 */
export function isStringType(type: string): boolean {
  return type === PhysicalColumnType.UTF8 || 
         type === PhysicalColumnType.STRING ||
         type === 'Text' ||
         type === 'Varchar';
}

/**
 * Helper function to check if a column type is a boolean type
 */
export function isBooleanType(type: string): boolean {
  return type === PhysicalColumnType.BOOL || 
         type === 'Boolean';
}

/**
 * Format options for logical types
 */
export interface LogicalTypeOptions {
  type: LogicalType;
  format?: string;
  precision?: number;
  timezone?: string;
}

/**
 * Represents the schema of a column
 */
export interface ColumnSchema {
  name: string;
  type: DataType;
  nullable: boolean;
  logicalType?: LogicalTypeOptions;
}

/**
 * Represents the schema of a table
 * @memoryUsage While schema itself is lightweight, it defines structure for potentially large datasets
 * Consider impact of column count on query performance
 */
export interface TableSchema {
  columns: ColumnSchema[];
}

/**
 * Available query operation types
 */
export enum QueryType {
  SELECT = 'SELECT',
  AGGREGATE = 'AGGREGATE',
  SQL = 'SQL'
}

/**
 * Column reference with type information
 */
export interface ColumnRef {
  name: string;
  schema: ColumnSchema;
}

/**
 * Sort direction for ORDER BY
 */
export enum SortDirection {
  ASC = 'ASC',
  DESC = 'DESC'
}

/**
 * Options for SELECT operations
 * @memoryUsage Result size scales with:
 * - Number of selected columns
 * - Row count after filtering
 * - Sort buffer size for ORDER BY
 */
export interface OrderByClause {
  column: ColumnRef;
  direction: SortDirection;
}

/**
 * Row limit options for queries
 */
export enum RowLimitType {
  FIXED = 'FIXED',
  SAMPLE_N = 'SAMPLE_N',
  SAMPLE_PERCENT = 'SAMPLE_PERCENT'
}

export interface RowLimit {
  type: RowLimitType;
  value: number;
}

/**
 * Options for SELECT operations
 */
export interface SelectQueryOptions {
  columns: ColumnRef[];
  where?: string;
  orderBy?: OrderByClause[];
  rowLimit?: RowLimit;
  windows?: WindowClause[];
}

/**
 * Aggregate operation types
 */
export enum AggregateOperation {
  COUNT = 'COUNT',
  SUM = 'SUM',
  AVG = 'AVG',
  MIN = 'MIN',
  MAX = 'MAX'
}

/**
 * Options for AGGREGATE operations
 * @memoryUsage Can be memory intensive:
 * - Memory scales with number of unique groups
 * - Each group maintains state for all aggregations
 * - Sort buffer needed for ORDER BY
 * Consider using HAVING to filter groups early
 */
export interface AggregateClause {
  operation: AggregateOperation;
  column: ColumnRef;
  alias: string;
  // For operations that need type casting (e.g., AVG on timestamps)
  castTo?: DataType;
}

/**
 * Options for AGGREGATE operations
 */
export interface AggregateQueryOptions {
  groupBy: ColumnRef[];
  aggregates: AggregateClause[];
  where?: string; // Pre-aggregation filter
  having?: string; // Post-aggregation filter
  orderBy?: OrderByClause[];
  rowLimit?: RowLimit; // Applied to final result
  innerRowLimit?: RowLimit; // Applied before aggregation
}

/**
 * Options for SQL operations
 * @memoryUsage Raw SQL queries can be arbitrarily complex:
 * - Memory usage depends on query complexity
 * - Joins can create large intermediate results
 * - Complex subqueries may duplicate data
 */
export interface SQLQueryOptions {
  sql: string;
  dialect: SQLDialect;
  parameters?: Record<string, unknown>;
}

/**
 * Union type of all possible query options
 * @memoryUsage Each query type has different memory characteristics
 * See individual type documentation for details
 */
export type QueryOptions = SelectQueryOptions | AggregateQueryOptions | SQLQueryOptions;

/**
 * Represents a query operation
 */
export interface Query {
  type: QueryType;
  options: QueryOptions;
}

export type JoinType = 'INNER' | 'LEFT' | 'RIGHT' | 'FULL';

/**
 * Helper to create a column reference with schema
 */
export function createColumnRef(name: string, schema: TableSchema): ColumnRef {
  const column = schema.columns.find(c => c.name === name);
  if (!column) {
    throw new Error(`Column not found: ${name}`);
  }
  return { name, schema: column };
}

/**
 * Window function types supported by the engine
 */
export enum WindowFunction {
  ROW_NUMBER = 'ROW_NUMBER',
  RANK = 'RANK',
  DENSE_RANK = 'DENSE_RANK',
  LAG = 'LAG',
  LEAD = 'LEAD',
  FIRST_VALUE = 'FIRST_VALUE',
  LAST_VALUE = 'LAST_VALUE'
}

/**
 * Window specification for window functions
 */
export interface WindowSpec {
  partitionBy?: ColumnRef[];
  orderBy?: OrderByClause[];
  frame?: {
    type: 'ROWS' | 'RANGE';
    start: 'UNBOUNDED PRECEDING' | 'CURRENT ROW' | number;
    end?: 'UNBOUNDED FOLLOWING' | 'CURRENT ROW' | number;
  };
}

/**
 * Window function clause
 */
export interface WindowClause {
  function: WindowFunction;
  column?: ColumnRef;  // Optional for functions like ROW_NUMBER
  alias: string;
  window: WindowSpec;
  args?: unknown[];  // For functions like LAG/LEAD that take arguments
}

/**
 * Options for materializing a view
 * @memoryUsage Controls how much data is materialized:
 * - Use page to get a subset of rows
 * - Small page sizes reduce memory usage
 * - Consider UI requirements when choosing page size
 * - Defaults to first 1000 rows if no options provided
 */
export interface MaterializeOptions {
  /**
   * Page of data to materialize
   * @example { size: 100, number: 0 } gets first 100 rows
   * @example { size: 50, number: 2 } gets rows 100-149
   * @default If not provided, defaults to { size: 1000, number: 0 }
   */
  page?: {
    /** Number of rows per page */
    size: number;
    /** Zero-based page number */
    number: number;
  };
}

export enum TableSourceType {
  LOCAL_FILE = 'LOCAL_FILE',
  URL = 'URL',
  DATATABLE = 'DATATABLE',
  MATERIALIZED = 'MATERIALIZED'
}

export enum TableFormat {
  PARQUET = 'PARQUET',
  CSV = 'CSV',
  JSON = 'JSON',
  ARROW = 'ARROW',
  TSV = 'TSV'
}

export type TableDataSource = 
  | {
      type: TableSourceType.LOCAL_FILE;
      fileId: string;
      format: TableFormat;
      schema: TableSchema;
      file?: File;
    }
  | {
      type: TableSourceType.URL;
      url: string;
      format: TableFormat;
      schema?: TableSchema;
    }
  | {
      type: TableSourceType.DATATABLE;
      url: string;
      format: TableFormat;
      schema?: TableSchema;
      materialized?: boolean;
      datatableId: string;
    }
  | {
      type: TableSourceType.MATERIALIZED;
      data: MaterializedTableData;
    }; 